import React from 'react'
import { t } from 'ttag'

import { useDeleteEvaluatorModal } from './useDeleteEvaluatorModal'

// NOTE: hook that exposes evaluator state and handlers
// for other local hooks and interview components
export const useEvaluator = (state, handlers) => {
  const { evaluation, evaluators, id, user } = state
  const {
    handleDeleteEvaluator,
    handleFetch,
    handlePostEvaluator,
    handleSetResponse,
    handleShowSnackbar,
  } = handlers

  const [evaluatorId, setEvaluatorId] = React.useState(null)

  const evaluator = React.useMemo(() => {
    const index = evaluators.findIndex(
      evaluator => evaluator?.id === evaluatorId,
    )

    return index >= 0 ? evaluators[index] : null
  }, [evaluatorId, evaluators])

  const handleAddEvaluator = React.useCallback(
    async ({ email, name }) => {
      const { error, meta, response } = await handlePostEvaluator({
        jobInterview: id,
        email,
        name,
      })

      if (response) {
        const { response } = await handleFetch({ uuid: id })

        handleSetResponse(response)
        handleShowSnackbar({
          message: t`The evaluator added successfully.`,
          type: 'success',
        })
      }

      return { error, meta, response }
    },
    [
      id,
      handleFetch,
      handlePostEvaluator,
      handleSetResponse,
      handleShowSnackbar,
    ],
  )

  const handleAddSelf = React.useCallback(() => {
    handleAddEvaluator({
      // @ts-expect-error the add handler reads email and name, not evaluators
      evaluators: [{ email: user?.email, name: user?.name }],
    })
  }, [user, handleAddEvaluator])

  const handleRemoveEvaluator = React.useCallback(
    async ({ uuid }) => {
      const { error, response } = await handleDeleteEvaluator({ uuid })

      if (response) {
        const { response } = await handleFetch({ uuid: id })

        handleSetResponse(response)
        handleShowSnackbar({
          message: t`The evaluator deleted successfully.`,
          type: 'success',
        })
      } else {
        console.log({ error })
      }
    },
    [
      id,
      handleDeleteEvaluator,
      handleFetch,
      handleSetResponse,
      handleShowSnackbar,
    ],
  )

  const handleSetEvaluatorId = React.useCallback(
    id => {
      setEvaluatorId(id)
    },
    [setEvaluatorId],
  )

  const hasEvaluators = evaluators.length > 0
  const isAlreadyIncluded = evaluators.some(
    ({ email }) => email === user?.email,
  )
  const isEvaluationAvailable = Boolean(evaluation)
  const isSelfAdded = isEvaluationAvailable || isAlreadyIncluded

  const {
    handleDeleteEvaluatorClose,
    handleDeleteEvaluatorOpen,
    handleDeleteEvaluatorConfirm,
    isDeleteEvaluatorOpen,
  } = useDeleteEvaluatorModal(
    { evaluator },
    { handleRemoveEvaluator, handleSetEvaluatorId },
  )

  return {
    state: {
      evaluator,
      hasEvaluators,
      isDeleteEvaluatorOpen,
      isSelfAdded,
    },
    handlers: {
      handleDeleteEvaluatorClose,
      handleDeleteEvaluatorOpen,
      handleDeleteEvaluatorConfirm,
      handleAddSelf,
    },
  }
}
